import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';
import PropertyQuickModal from './PropertyQuickModal';
import { wishlistService } from '../../../services/wishlistService';
import { useAuth } from '../../../contexts/AuthContext';

const FeaturedProperties = () => {
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickViewProperty, setQuickViewProperty] = useState(null);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [wishlistLoading, setWishlistLoading] = useState(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();

  const touchStartY = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    const loadFeaturedProperties = async () => {
      try {
        const { data, error: fetchError } = await featuredPropertiesService?.getActiveFeaturedProperties();
        if (fetchError) {setError(fetchError);return;}
        const transformedData = data?.map((item) => ({
          id: item?.property_id,
          title: item?.custom_headline || item?.property_title,
          location: `${item?.commune_name || 'Abidjan'}`,
          price: (item?.promotional_price || item?.property_price)?.toString(),
          currency: "XOF",
          marketType: item?.listing_type || item?.property_market_type || 'sale',
          rentalPeriod: item?.property_rental_period,
          bedrooms: item?.property_bedrooms || 0,
          bathrooms: item?.property_bathrooms || 0,
          area: item?.property_surface_area || 0,
          type: item?.property_type || 'Propriété',
          image: item?.hero_image_url || item?.property_images?.[0] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2075&q=80",
          videoThumbnail: item?.property_images?.[1] || item?.hero_image_url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2053&q=80",
          isNew: false,
          featured: true,
          promotionalText: item?.promotional_text,
          featuredPropertyId: item?.id,
          clickCount: item?.click_count || 0,
          inquiryCount: item?.inquiry_count || 0
        })) || [];
        setFeaturedProperties(transformedData);
      } catch (err) {
        setError(`Erreur de chargement: ${err?.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadFeaturedProperties();
  }, []);

  useEffect(() => {
    if (!user?.id) {setWishlistIds(new Set());return;}
    wishlistService?.getWishlistIds(user?.id)?.then(({ data }) => {
      setWishlistIds(new Set(data || []));
    });
  }, [user?.id]);

  const handlePropertyClick = async (property) => {
    if (property?.featuredPropertyId) {
      try {
        await featuredPropertiesService?.incrementClickCount(property?.featuredPropertyId);
      } catch (err) {
        console.log('Erreur lors du suivi des clics:', err);
      }
    }
  };

  const handleWishlistToggle = async (e, property) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user?.id) return;
    const propId = property?.id?.toString();
    setWishlistLoading((prev) => new Set([...prev, propId]));
    try {
      const { added } = await wishlistService?.toggleWishlist(user?.id, property);
      setWishlistIds((prev) => {
        const next = new Set(prev);
        if (added) next?.add(propId);else
        next?.delete(propId);
        return next;
      });
    } catch (err) {
      console.error('Wishlist toggle error:', err);
    } finally {
      setWishlistLoading((prev) => {const next = new Set(prev);next?.delete(propId);return next;});
    }
  };

  const formatPrice = (price, marketType, rentalPeriod, currency = 'XOF') => {
    const numPrice = parseInt(price);
    if (!numPrice || isNaN(numPrice)) return `Prix sur demande`;
    if (marketType === 'rental') {
      const periodSuffix = rentalPeriod === 'monthly' ? '/mois' :
      rentalPeriod === 'yearly' ? '/an' :
      rentalPeriod === 'daily' ? '/jour' : '/mois';
      if (numPrice >= 1000000) return `${(numPrice / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M ${currency}${periodSuffix}`;
      if (numPrice >= 1000) return `${(numPrice / 1000)?.toFixed(0)}K ${currency}${periodSuffix}`;
      return `${numPrice?.toLocaleString()} ${currency}${periodSuffix}`;
    }
    if (numPrice >= 1000000) return `${(numPrice / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M ${currency}`;
    if (numPrice >= 1000) return `${(numPrice / 1000)?.toFixed(0)}K ${currency}`;
    return `${numPrice?.toLocaleString()} ${currency}`;
  };

  const handleTouchStart = (e) => {
    touchStartY.current = e?.touches?.[0]?.clientY;
    touchStartX.current = e?.touches?.[0]?.clientX;
  };

  const handleTouchEnd = (e, property) => {
    if (touchStartY?.current === null) return;
    const deltaY = touchStartY?.current - e?.changedTouches?.[0]?.clientY;
    const deltaX = Math.abs(touchStartX?.current - e?.changedTouches?.[0]?.clientX);
    if (deltaY > 40 && deltaY > deltaX) setQuickViewProperty(property);
    touchStartY.current = null;
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-transparent border-none">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Propriétés en Vedette
            </h2>
            <div className="flex justify-center">
              <Icon name="Loader2" size={24} className="animate-spin text-brand-gold" />
            </div>
          </div>
        </div>
      </section>);
  }

  if (error) {
    return (
      <section className="py-16 lg:py-24 bg-transparent border-none">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Propriétés en Vedette
            </h2>
            <div className="bg-red-500/10 border-none rounded-xl p-5 max-w-md mx-auto">
              <Icon name="AlertCircle" size={20} className="text-red-400 mx-auto mb-2" />
              <p className="text-red-200 text-sm font-body">{error}</p>
            </div>
          </div>
        </div>
      </section>);
  }

  if (featuredProperties?.length === 0) {
    return (
      <section className="py-16 lg:py-24 bg-transparent border-none">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Propriétés en Vedette
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto mb-8 font-body">
              Aucune propriété en vedette disponible pour le moment
            </p>
          </div>
        </div>
      </section>);
  }

  return (
    <>
      <section className="py-12 sm:py-16 lg:py-20 bg-transparent border-none outline-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* En-tête de section */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-body font-semibold text-brand-gold uppercase tracking-widest mb-3">
                Sélection exclusive
              </p>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white tracking-tight">
                Propriétés en Vedette
              </h2>
              <p className="text-sm text-white/60 font-body mt-2 max-w-md font-light">
                Découvrez l'élite de l'immobilier ivoirien. Visitez chaque espace en vidéo immersive avant de vous décider.
              </p>
            </div>
            <Link to="/property-search-listings" className="flex-shrink-0">
              <Button 
                variant="outline" 
                size="sm" 
                iconName="ArrowRight" 
                iconPosition="right"
                className="bg-white/5 hover:bg-white/10 text-white border-none rounded-full px-6 transition-all"
              >
                Tout explorer
              </Button>
            </Link>
          </div>

          <div className="flex items-center gap-2 mb-6 sm:hidden opacity-60">
            <Icon name="ChevronsUp" size={14} className="text-white" />
            <span className="text-xs text-white font-body">Glissez vers le haut pour l'aperçu</span>
          </div>

          {/* Grille de Propriétés (Zéro Bordure, Effet Glass) */}
          <div
            className="flex overflow-x-auto snap-x snap-mandatory gap-5 pb-6 -mx-4 px-4 sm:grid sm:grid-cols-2 sm:overflow-visible sm:snap-none sm:mx-0 sm:px-0 lg:grid lg:grid-cols-4 lg:gap-8 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {featuredProperties?.map((property) =>
            <div
              key={property?.id}
              className="flex-none w-[82vw] max-w-[320px] snap-start sm:w-auto sm:max-w-none sm:flex-auto bg-[#111111] rounded-2xl overflow-hidden shadow-2xl hover:shadow-brand-gold/10 transition-all duration-500 group border-none outline-none transform hover:-translate-y-2 relative"
              onMouseEnter={() => setHoveredProperty(property?.id)}
              onMouseLeave={() => setHoveredProperty(null)}
              onTouchStart={handleTouchStart}
              onTouchEnd={(e) => handleTouchEnd(e, property)}
            >
                {/* Image de la propriété */}
                <div className="relative h-56 overflow-hidden">
                  <Image
                  src={hoveredProperty === property?.id ? property?.videoThumbnail : property?.image}
                  alt={property?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  
                  {/* Overlay assombrissant subtil sur l'image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-80" />

                  <div className="absolute top-4 left-4 flex gap-2">
                    {property?.isNew &&
                      <span className="bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold">
                        Nouveau
                      </span>
                    }
                    {property?.featured &&
                      <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-white shadow-lg ${
                        property?.marketType === 'rental' ? 'bg-blue-600/90 backdrop-blur-md' : 'bg-brand-gold/90 backdrop-blur-md'}`
                      }>
                        {property?.marketType === 'rental' ? 'À louer' : 'À vendre'}
                      </span>
                    }
                  </div>

                  {/* Bouton Favoris (Wishlist) */}
                  {user &&
                    <button
                      onClick={(e) => handleWishlistToggle(e, property)}
                      disabled={wishlistLoading?.has(property?.id?.toString())}
                      className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center transition-all duration-200 border-none outline-none hover:bg-black/60"
                      aria-label="Favoris"
                    >
                      {wishlistLoading?.has(property?.id?.toString()) ?
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                        <Icon
                          name="Heart"
                          size={16}
                          color={wishlistIds?.has(property?.id?.toString()) ? '#e11d48' : 'white'}
                          fill={wishlistIds?.has(property?.id?.toString()) ? '#e11d48' : 'none'} 
                        />
                      }
                    </button>
                  }

                  {/* Icône Play Video centrale au survol */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-black/50 backdrop-blur-md rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <Icon name="Play" size={24} color="white" />
                    </div>
                  </div>

                  {/* Prix (Style Apple: Sans bordure, gros texte) */}
                  <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-white font-heading font-bold text-xl drop-shadow-md">
                        {formatPrice(property?.price, property?.marketType, property?.rentalPeriod, property?.currency)}
                      </span>
                  </div>
                </div>

                {/* Détails de la propriété */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="font-heading font-semibold text-lg text-white mb-2 line-clamp-1 group-hover:text-brand-gold transition-colors">
                      {property?.title}
                    </h3>
                    <div className="flex items-center text-white/50 text-sm font-body">
                      <Icon name="MapPin" size={14} className="mr-2" />
                      {property?.location}
                    </div>
                  </div>

                  {property?.promotionalText &&
                    <div className="mb-4">
                      <p className="text-sm text-brand-gold/80 italic font-light line-clamp-1">
                        {property?.promotionalText}
                      </p>
                    </div>
                  }

                  {/* Icônes de caractéristiques */}
                  <div className="flex items-center gap-5 text-sm text-white/60 mb-6 font-body font-light">
                    <div className="flex items-center gap-1.5">
                      <Icon name="Bed" size={16} className="text-white/40" />
                      {property?.bedrooms}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="Bath" size={16} className="text-white/40" />
                      {property?.bathrooms}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="Square" size={16} className="text-white/40" />
                      {property?.area}m²
                    </div>
                  </div>

                  {/* Boutons d'action : Style premium, arrondis et fluides */}
                  <div className="flex gap-3">
                    <Link
                      to={`/individual-property-page/${property?.id}`}
                      className="flex-1"
                      onClick={() => handlePropertyClick(property)}>
                      <Button variant="default" size="sm" fullWidth className="bg-white text-black hover:bg-gray-200 rounded-xl font-medium border-none shadow-none">
                        Détails
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Zap"
                      onClick={() => setQuickViewProperty(property)}
                      title="Aperçu rapide" 
                      className="bg-white/5 text-white border-none hover:bg-white/10 rounded-xl shadow-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bandeau d'Appel à l'Action Vidéo (Glow Effect) */}
          <div className="mt-16 sm:mt-24 text-center relative group">
            {/* Halo lumineux caché derrière la carte */}
            <div className="absolute inset-0 bg-brand-gold/5 blur-3xl rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative bg-gradient-to-b from-[#111] to-[#0A0A0A] rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto border-none shadow-2xl">
              <div className="flex flex-col sm:flex-row items-center justify-center mb-8 gap-5">
                <div className="bg-gradient-to-br from-brand-gold to-yellow-600 rounded-2xl p-4 shadow-lg shadow-brand-gold/20">
                  <Icon name="Video" size={32} color="white" />
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="font-heading font-bold text-2xl text-white mb-2 tracking-tight">
                    L'Expérience Vidéo Immersive
                  </h3>
                  <p className="text-white/60 font-body text-base max-w-md font-light">
                    Ne perdez plus votre temps dans les embouteillages. Visitez chaque recoin en haute définition depuis votre salon.
                  </p>
                </div>
              </div>
              <Button 
                variant="default" 
                size="lg"
                iconName="Play" 
                iconPosition="left" 
                onClick={() => navigate('/video-tour-discovery-center')}
                className="bg-white text-black hover:bg-gray-100 rounded-full px-8 font-semibold shadow-lg hover:scale-105 transition-transform border-none"
              >
                Lancer les visites
              </Button>
            </div>
          </div>

          {/* Carrousel de Logos Partenaires */}
          <div className="mt-20 sm:mt-28">
            <p className="text-center text-xs font-body font-semibold text-white/30 uppercase tracking-widest mb-10">
              Ils bâtissent la confiance avec nous
            </p>
            <div className="relative overflow-hidden">
              {/* Correction vitale : Les dégradés utilisent le noir absolu du fond */}
              <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />
              
              <div className="flex items-center gap-16 sm:gap-24 animate-logo-ticker whitespace-nowrap">
                {[1, 2, 3]?.map((set) =>
                <div key={set} className="flex items-center gap-16 sm:gap-24 shrink-0">
                    <img src="/assets/images/CIGALIDAR-1781022087599.png" alt="CIGA LIDAR" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                    <img src="/assets/images/ISIS_IMMOBILIER-1781022087333.png" alt="ISIS Immobilier" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                    <img src="/assets/images/SDLG-1781022087600.png" alt="SDLG" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                    <img src="/assets/images/ALUTRACO-1781022430897.png" alt="ALUTRACO" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                    <img src="/assets/images/CHINASTARGLASS-1781022577753.png" alt="CHINASTARGLASS" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                    <img src="/assets/images/VOIREBAT-1781022614602.png" alt="VOIREBAT" className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-100 transition-opacity duration-500 filter grayscale hover:grayscale-0" />
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </section>
      {quickViewProperty &&
      <PropertyQuickModal
        property={quickViewProperty}
        onClose={() => setQuickViewProperty(null)}
        formatPrice={formatPrice} />
      }
    </>
  );
};

export default FeaturedProperties;